/**
 * WeGotNuts Theme - Global Contact Us Floating Trigger & Modal Controller
 * Accessible, responsive, zero-dependency Vanilla JS module.
 *
 * Defects fixed (2026-08-10):
 *  #1 - Close animation: setAttribute('hidden') is now deferred until after
 *       the CSS transition completes via transitionend + fallback timeout.
 *       Open animation gets a forced reflow so the pre-transition state is
 *       established before .is-open is applied.
 *  #2 - Post-submission auto-open removed entirely. contact[source] is
 *       unconditionally present in the modal DOM making detection always
 *       truthy — removing is safer than producing false positives on the
 *       standard /pages/contact-us page form.
 *  #3 - Event listener accumulation: document keydown is bound exactly once
 *       per page session via _docKeydownBound flag. Element-level listeners
 *       (FAB click, close triggers) are tracked and removed before rebinding
 *       on each init(), making re-initialization fully idempotent.
 */
(function () {
  'use strict';

  var WgnContactModal = {
    fab: null,
    modal: null,
    dialog: null,
    lastActiveElement: null,
    isOpen: false,

    // Defect #1: reference to the close-animation fallback timer.
    _closeTimer: null,

    // Defect #3: listener tracking to prevent accumulation on re-init.
    _docKeydownBound: false,
    _keydownHandler: null,
    _boundFabClick: null,
    _boundCloseClicks: [],

    init: function () {
      this.fab   = document.getElementById('WgnContactFab');
      this.modal = document.getElementById('WgnContactModal');

      if (!this.modal) return;

      this.dialog = this.modal.querySelector('.wgn-contact-modal__dialog');

      this.bindEvents();
      // NOTE (Defect #2): checkPostSubmission() has been intentionally removed.
      // The hidden contact[source] input is rendered unconditionally inside the
      // modal DOM; querying for it therefore always returns non-null, causing the
      // modal to open incorrectly after every standalone /pages/contact-us form
      // submission. No reliable server-side signal exists without URL params or
      // session storage. Removing auto-open is the correct production behavior.
    },

    bindEvents: function () {
      var self = this;

      // ---- FAB click ----------------------------------------------------------
      // Defect #3: Remove the previous listener before adding the new one so
      // repeated init() calls (e.g., after shopify:section:load) do not stack
      // duplicate handlers on the same element.
      if (this.fab) {
        if (this._boundFabClick) {
          this.fab.removeEventListener('click', this._boundFabClick);
        }
        this._boundFabClick = function (e) {
          e.preventDefault();
          self.open();
        };
        this.fab.addEventListener('click', this._boundFabClick);
      }

      // ---- Close triggers (close button + backdrop) ---------------------------
      // Defect #3: Explicitly remove every previously-tracked close listener
      // before discovering and rebinding fresh ones from the current DOM.
      if (this._boundCloseClicks.length) {
        for (var r = 0; r < this._boundCloseClicks.length; r++) {
          var pair = this._boundCloseClicks[r];
          pair.el.removeEventListener('click', pair.fn);
        }
        this._boundCloseClicks = [];
      }
      var closeEls = this.modal.querySelectorAll('[data-wgn-modal-close]');
      for (var i = 0; i < closeEls.length; i++) {
        (function (el) {
          var fn = function (e) {
            e.preventDefault();
            self.close();
          };
          el.addEventListener('click', fn);
          self._boundCloseClicks.push({ el: el, fn: fn });
        })(closeEls[i]);
      }

      // ---- Document keydown (ESC + focus trap) --------------------------------
      // Defect #3: Register on document exactly once per page session.
      // The _docKeydownBound flag persists across init() calls so subsequent
      // shopify:section:load events cannot add a second listener.
      if (!this._docKeydownBound) {
        this._keydownHandler = function (e) {
          if (!self.isOpen) return;

          var isEscape = e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27;
          var isTab    = e.key === 'Tab'    || e.keyCode === 9;

          if (isEscape) {
            e.preventDefault();
            self.close();
          } else if (isTab) {
            self.trapFocus(e);
          }
        };
        document.addEventListener('keydown', this._keydownHandler);
        this._docKeydownBound = true;
      }
    },

    open: function () {
      if (this.isOpen) return;

      // Defect #1: Cancel any in-flight close timer. Without this, a rapid
      // close -> open sequence would leave a pending applyHidden() that fires
      // after the modal has already been reopened, permanently hiding it.
      if (this._closeTimer) {
        clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }

      this.lastActiveElement = document.activeElement;
      this.isOpen = true;

      // Step 1: Remove hidden so the element enters the render tree.
      //         At this point opacity:0 and transform:scale(0.95) apply.
      this.modal.removeAttribute('hidden');

      // Step 2: Force a synchronous reflow. This ensures the browser has
      //         computed the pre-transition state before .is-open changes
      //         the computed values. Without this, both DOM mutations can
      //         land in the same paint frame and the open transition may be
      //         skipped on Chrome, Firefox, and Safari.
      // eslint-disable-next-line no-unused-expressions
      this.modal.offsetHeight;

      // Step 3: Apply .is-open — CSS transition fires from here.
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.classList.add('is-open');

      if (this.fab) {
        this.fab.setAttribute('aria-expanded', 'true');
      }

      document.body.classList.add('wgn-modal-open');

      // Move focus into the modal after the open transition has started.
      var firstInput = this.modal.querySelector('#modal-ContactFormName') ||
                       this.modal.querySelector('.wgn-form-input') ||
                       this.modal.querySelector('#WgnContactModalClose');

      if (firstInput) {
        setTimeout(function () {
          firstInput.focus();
        }, 50);
      }
    },

    close: function () {
      if (!this.isOpen) return;

      var self = this;

      this.isOpen = false;

      // Step 1: Remove .is-open — CSS transition begins (opacity 1->0,
      //         transform scale(1)->scale(0.95), duration 300ms per CSS).
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');

      if (this.fab) {
        this.fab.setAttribute('aria-expanded', 'false');
      }

      // Remove body scroll lock immediately (no visual dependency on timing).
      document.body.classList.remove('wgn-modal-open');

      // Restore keyboard focus immediately so the user is not stranded.
      if (this.lastActiveElement &&
          typeof this.lastActiveElement.focus === 'function') {
        this.lastActiveElement.focus();
      } else if (this.fab) {
        this.fab.focus();
      }

      // Defect #1: Do NOT call setAttribute('hidden') synchronously.
      //
      // The CSS rule `.wgn-contact-modal[hidden] { display: none !important }`
      // takes effect the moment the attribute is set. If hidden is set in the
      // same JS frame as classList.remove('is-open'), the browser collapses
      // both mutations into one paint and the close transition never renders.
      //
      // Solution: defer hidden until after the CSS transition completes.
      //   Primary:  transitionend listener scoped to the modal element's own
      //             opacity transition (not child element bubbles).
      //   Fallback: setTimeout at CSS duration (300ms) + 50ms buffer = 350ms,
      //             for cases where transitionend does not fire (hidden tab,
      //             GPU acceleration disabled, etc).
      //
      // Race-condition guard: applyHidden() checks !isOpen before applying
      // the attribute, so a rapid reopen between close() and the
      // timer/transitionend firing cannot hide the freshly-opened modal.

      var reduceMotion = window.matchMedia &&
                         window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      var applyHidden = function () {
        // Guard: only hide if the modal is still in the closed state.
        if (!self.isOpen) {
          self.modal.setAttribute('hidden', '');
        }
      };

      if (reduceMotion) {
        // No transition; apply immediately (matches CSS prefers-reduced-motion rule).
        applyHidden();
        return;
      }

      // Wait for CSS transition to finish before applying hidden.
      var handled = false;
      var transitionHandler = function (e) {
        // Filter to this modal element only — child element transitions
        // also bubble through transitionend and must be ignored.
        if (e.target !== self.modal) return;
        if (handled) return;
        handled = true;
        self.modal.removeEventListener('transitionend', transitionHandler);
        clearTimeout(self._closeTimer);
        self._closeTimer = null;
        applyHidden();
      };
      self.modal.addEventListener('transitionend', transitionHandler);

      // Fallback: CSS transition is 300ms; allow 350ms before forcing hidden.
      self._closeTimer = setTimeout(function () {
        if (handled) return; // transitionend already fired; nothing to do.
        self.modal.removeEventListener('transitionend', transitionHandler);
        self._closeTimer = null;
        applyHidden();
      }, 350);
    },

    trapFocus: function (e) {
      if (!this.modal) return;

      var focusables = this.modal.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]),' +
        ' input:not([disabled]), select:not([disabled]),' +
        ' [tabindex]:not([tabindex="-1"])'
      );

      if (!focusables.length) return;

      var first = focusables[0];
      var last  = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  };

  // ----- Boot ----------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      WgnContactModal.init();
    });
  } else {
    WgnContactModal.init();
  }

  // Shopify Theme Editor section reload support.
  // Defect #3: init() is now fully idempotent:
  //   - Element listeners (FAB, close) are removed then rebound on each call.
  //   - The document keydown listener is registered only once (_docKeydownBound).
  //   - _closeTimer / isOpen state is preserved safely across re-inits.
  document.addEventListener('shopify:section:load', function () {
    WgnContactModal.init();
  });

})();

